import { useMemo } from 'react'
import {
  DeepRequired,
  FieldErrorsImpl,
  FieldValues,
  GlobalError,
} from 'react-hook-form'
import { Alert, List, Text, Title } from '@mantine/core'
import flattenDeep from 'lodash/flattenDeep'
import isString from 'lodash/isString'

export type FormErrorsProps<T extends FieldValues> = {
  errors:
    | FieldErrorsImpl<T>
    | (Partial<FieldErrorsImpl<DeepRequired<FieldValues>>> & {
        root?: Record<string, GlobalError> & GlobalError
      })
    | string
    | undefined
    | any // TODO: remove any once figure out how to handle this type
  title?: string
}

/**
 * Recursively extracts all error message strings from a nested structure.
 */
const extractMessages = (err: any): string[] => {
  if (!err) {
    return []
  }
  if (isString(err)) {
    return [err]
  }
  // Handle react-hook-form's specific error structure or any object with a message
  if (typeof err === 'object' && err.message && isString(err.message)) {
    return [err.message]
  }
  // Recurse into arrays or objects
  if (typeof err === 'object' && err !== null) {
    return Object.values(err).flatMap(extractMessages)
  }
  return []
}

export const FormErrors = <T extends FieldValues>({
  errors,
  title,
}: FormErrorsProps<T>) => {
  const alertTitle = useMemo(
    () => title || 'We have detected an error in the data you provided:',
    [title],
  )

  const errorMessages = flattenDeep(extractMessages(errors))

  if (errorMessages.length === 0) {
    return null
  }

  return (
    <Alert
      title={
        <Title order={6} color="gray.9">
          {alertTitle}
        </Title>
      }
      color="red.3"
      variant="filled"
    >
      <List withPadding style={{ listStyle: 'disc' }}>
        {/* Map over the flattened list of error messages */}
        {errorMessages.map((message, index) => (
          <List.Item key={`${message}-${index}`}>
            {' '}
            {/* Improve key uniqueness */}
            <Text color="gray.9">{message}</Text>
          </List.Item>
        ))}
      </List>
    </Alert>
  )
}
